import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { DATASOURCE_TYPE_TO_TAB } from '@/modules/collections/constants';
import { getIconForDatasourceType } from '@/modules/collections/helper';
import { selectAccountSlug } from '@/redux/session/selectors';
import { CollectionContent, DatasourceType } from '@/shared/types/collections';

interface TypeCountsCellProps {
  collectionId: string;
  content?: CollectionContent;
}

interface TypeCount {
  icon: React.ReactNode;
  count: number;
  type: DatasourceType;
}

const getIcon = (type: DatasourceType) => {
  const Icon = getIconForDatasourceType(type);
  return Icon && <Icon size={16} />;
};

/**
 * Converts CollectionContent to TypeCount array for display.
 * Counts match the backend SQL view:
 * - website/external_sources: count distinct datasources
 * - faq/files: count documents
 */
function contentToTypeCounts(content: CollectionContent | undefined): TypeCount[] {
  if (!content) {
    return [];
  }

  const result: TypeCount[] = [];

  // Order: FAQ, FILES, WEBSITE, external sources
  if (content.faq > 0) {
    result.push({
      icon: getIcon(DatasourceType.FAQ),
      count: content.faq,
      type: DatasourceType.FAQ,
    });
  }

  if (content.files > 0) {
    result.push({
      icon: getIcon(DatasourceType.FILES),
      count: content.files,
      type: DatasourceType.FILES,
    });
  }

  if (content.website > 0) {
    result.push({
      icon: getIcon(DatasourceType.WEBSITE),
      count: content.website,
      type: DatasourceType.WEBSITE,
    });
  }

  if (content.external_sources > 0) {
    // external_sources includes all external KB types (ZENDESK_KB, INTERCOM_KB, WORKVIVO_KB, GDRIVE).
    // Use ZENDESK_KB as the representative type since they all route to the 'external' tab.
    result.push({
      icon: getIcon(DatasourceType.ZENDESK_KB),
      count: content.external_sources,
      type: DatasourceType.ZENDESK_KB,
    });
  }

  return result;
}

/**
 * Renders the per-type icon and counter for a collection's content, with tooltips
 * indicating the corresponding tab category (e.g. FAQ, Documents, Webpages, External).
 */
export function TypeCountsCell({ collectionId, content }: TypeCountsCellProps) {
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  const typeCounts = contentToTypeCounts(content);
  const { t } = useTranslation();

  if (typeCounts.length === 0) {
    return <span className="text-muted-foreground">{t('common.no_data')}</span>;
  }

  const handleClick = (type: DatasourceType) => {
    const category = DATASOURCE_TYPE_TO_TAB[type];
    if (!category) {
      return;
    }
    navigate(`/${slug}/knowledge-bases/${collectionId}/category/${category}`);
  };

  return (
    <div className="flex items-center gap-2">
      {typeCounts.map((item) => {
        const category = DATASOURCE_TYPE_TO_TAB[item.type];
        const label = category ? t(`collections.tabs.${category}`) : '';
        return (
          <InfoTooltip key={item.type} content={label} size="auto">
            <button
              className="flex items-center gap-1 text-muted-foreground hover:text-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClick(item.type);
              }}
            >
              <span className="flex h-4 w-4 items-center justify-center">{item.icon}</span>
              <span>{item.count}</span>
            </button>
          </InfoTooltip>
        );
      })}
    </div>
  );
}
